import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { URL_EXTENSION } from '@utils/constants/app-constants';
import { BaseError } from '@utils/types/shared/base';
import {
  UsageRevenueResponse,
  CustomerTypeFilter
} from '@utils/types/back-office/usage-revenue';

/**
 * Hook to fetch detailed usage data for all customers
 *
 * GET /api/admin/usage/detailed
 * Query params:
 *   - month (YYYY-MM, optional — overrides period_start/period_end)
 *   - customer_type: "all" | "direct" | "reseller" (default: "all")
 *   - sort: "total_cost" | "organization" | "organization_id" | "plan_name" | "stt_units" | "translation_characters"
 *   - direction: "asc" | "desc"
 *   - limit, offset
 *   - organization_id (filter by exact org id)
 *   - q (filter by org name)
 *   - plan_name (filter by plan)
 *   - min_stt_units (filter by minimum usage)
 */
export const useGetUsageDetailed = (
  month: string | undefined,
  options?: {
    customerType?: CustomerTypeFilter;
    limit?: number;
    offset?: number;
    sort?: string;
    direction?: 'asc' | 'desc';
    search?: string;
    organizationId?: number;
    planName?: string;
    minUsage?: number;
  }
) => {
  const {
    customerType = 'all',
    limit = 25,
    offset = 0,
    sort,
    direction,
    search,
    organizationId,
    planName,
    minUsage
  } = options || {};

  const fetchUsageDetailed = async () => {
    if (!month) {
      throw new Error('Month is required');
    }

    const params = new URLSearchParams({
      month,
      customer_type: customerType,
      limit: String(limit),
      offset: String(offset)
    });

    if (sort) params.append('sort', sort);
    if (direction) params.append('direction', direction);
    if (search) params.append('q', search);
    if (organizationId != null)
      params.append('organization_id', String(organizationId));
    if (planName) params.append('plan_name', planName);
    if (minUsage != null) params.append('min_stt_units', String(minUsage));

    const response = await Axios.get<UsageRevenueResponse>(
      `${URL_EXTENSION}/admin/usage/detailed?${params.toString()}`
    );
    return response.data;
  };

  return useQuery<UsageRevenueResponse, AxiosError<BaseError>>(
    [
      'usageDetailed',
      month,
      customerType,
      limit,
      offset,
      sort,
      direction,
      search,
      organizationId,
      planName,
      minUsage
    ],
    fetchUsageDetailed,
    {
      enabled: !!month,
      useErrorBoundary: false,
      keepPreviousData: true
    }
  );
};
